import { useCallback, useEffect, useMemo, useState } from "react";
import {
	connectionStatusForMatrix,
	listConnections,
	LIVE_CONNECT_PROVIDERS,
	type PlatformConnection,
} from "./connections-api.js";
import {
	COMMS_CHANNELS,
	CONNECTORS,
	type Integration,
	type IntegrationStatus,
} from "./nav.js";
import { usePlatformSessionReady } from "./PlatformSession.js";

export type ConnectionsState =
	| { status: "loading" }
	| { status: "ready"; providers: PlatformConnection[] }
	| { status: "error"; message: string };

function withLiveStatus(
	items: readonly Integration[],
	providers: PlatformConnection[],
): Integration[] {
	return items.map((item) => {
		const live = connectionStatusForMatrix(providers, item.id);
		if (!live) return { ...item };
		return { ...item, status: live satisfies IntegrationStatus };
	});
}

export function usePlatformConnections(enabled: boolean) {
	const sessionReady = usePlatformSessionReady();
	const [state, setState] = useState<ConnectionsState>({ status: "loading" });

	const refresh = useCallback(async () => {
		if (!enabled || !sessionReady) return;
		const listed = await listConnections();
		if (!listed.ok) {
			setState({ status: "error", message: listed.error });
			return;
		}
		setState({ status: "ready", providers: listed.providers });
	}, [enabled, sessionReady]);

	useEffect(() => {
		if (!enabled || !sessionReady) return;
		void refresh();
	}, [enabled, sessionReady, refresh]);

	const providers =
		state.status === "ready" ? state.providers : ([] as PlatformConnection[]);

	const connectors = useMemo(
		() => withLiveStatus(CONNECTORS, providers),
		[providers],
	);
	const channels = useMemo(
		() => withLiveStatus(COMMS_CHANNELS, providers),
		[providers],
	);

	const providerFor = useCallback(
		(matrixId: string): PlatformConnection | null => {
			const row = providers.find(
				(p) =>
					p.id === matrixId ||
					(matrixId === "gsc" && p.id === "google_search_console") ||
					(matrixId === "email" && p.id === "gmail"),
			);
			return row ?? null;
		},
		[providers],
	);

	return useMemo(
		() => ({
			state,
			refresh,
			connectors,
			channels,
			providerFor,
			isLiveConnect: (id: string) => LIVE_CONNECT_PROVIDERS.has(id),
		}),
		[state, refresh, connectors, channels, providerFor],
	);
}
